/**
 * Field Validation Tab
 * 
 * Configure validation rules for fields.
 * Uses a filter-like interface similar to the reference implementation.
 * 
 * Format: { _and: [{ field: { _operator: value } }, ...] }
 */

import { useState, useEffect, useMemo } from 'react';
import { Info, Plus, X, GripVertical } from 'lucide-react';
import type { Permission } from '@/stores/access-control';
import { useFieldsStore } from '@/stores/fields';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';

interface FieldValidationTabProps {
    permission: Permission;
    localPermission: Partial<Permission>;
    policyName: string;
    onUpdate: (updates: Partial<Permission>) => void;
}

// Validation operators with their display labels (matching reference en-US.yaml)
const VALIDATION_OPERATORS: Record<string, string> = {
    '_eq': 'Equals',
    '_neq': "Doesn't equal",
    '_lt': 'Less than',
    '_lte': 'Less than or equal to',
    '_gt': 'Greater than',
    '_gte': 'Greater than or equal to',
    '_contains': 'Contains',
    '_ncontains': "Doesn't contain",
    '_icontains': 'Contains (insensitive)',
    '_starts_with': 'Starts with',
    '_istarts_with': 'Starts with (insensitive)',
    '_nstarts_with': "Doesn't start with",
    '_nistarts_with': "Doesn't start with (insensitive)",
    '_ends_with': 'Ends with',
    '_iends_with': 'Ends with (insensitive)',
    '_nends_with': "Doesn't end with",
    '_niends_with': "Doesn't end with (insensitive)",
    '_in': 'Is one of',
    '_nin': 'Is not one of',
    '_between': 'Is between',
    '_nbetween': "Isn't between",
    '_null': 'Is null',
    '_nnull': "Isn't null",
    '_empty': 'Is empty',
    '_nempty': "Isn't empty",
    '_regex': 'Matches RegExp',
    '_submitted': 'Is submitted',
};

// Operators that don't need a value input
const BOOLEAN_OPERATORS = ['_null', '_nnull', '_empty', '_nempty', '_submitted'];

// Operators that need array input
const ARRAY_OPERATORS = ['_in', '_nin', '_between', '_nbetween'];

// Get operators for a field type (with validation operators included)
function getOperatorsForType(type: string): string[] {
    const baseOperators = ['_eq', '_neq', '_null', '_nnull', '_empty', '_nempty'];

    switch (type) {
        case 'string':
        case 'text':
        case 'uuid':
            return [
                ...baseOperators,
                '_contains', '_ncontains', '_icontains',
                '_starts_with', '_nstarts_with', '_istarts_with', '_nistarts_with',
                '_ends_with', '_nends_with', '_iends_with', '_niends_with',
                '_in', '_nin', '_regex', '_submitted'
            ];
        case 'integer':
        case 'bigInteger':
        case 'float':
        case 'decimal':
            return [
                ...baseOperators,
                '_lt', '_lte', '_gt', '_gte',
                '_in', '_nin', '_between', '_nbetween', '_submitted'
            ];
        case 'boolean':
            return ['_eq', '_neq', '_null', '_nnull', '_submitted'];
        case 'timestamp':
        case 'dateTime':
        case 'date':
        case 'time':
            return [
                ...baseOperators,
                '_lt', '_lte', '_gt', '_gte',
                '_between', '_nbetween', '_submitted'
            ];
        case 'json':
            return ['_null', '_nnull', '_empty', '_nempty', '_submitted'];
        default:
            return [...baseOperators, '_submitted'];
    }
}

interface ValidationRule {
    id: string;
    field: string;
    operator: string;
    value: unknown;
}

// Parse validation object to rules
function parseValidationToRules(validation: Record<string, unknown> | null | undefined): ValidationRule[] {
    if (!validation || Object.keys(validation).length === 0) return [];

    const rules: ValidationRule[] = [];

    // Handle _and format: { _and: [{ field: { _op: val } }, ...] }
    if ('_and' in validation && Array.isArray(validation._and)) {
        for (const item of validation._and) {
            if (typeof item === 'object' && item !== null) {
                const entries = Object.entries(item as Record<string, unknown>);
                for (const [field, condition] of entries) {
                    if (field !== '_and' && field !== '_or' && typeof condition === 'object' && condition !== null) {
                        const condEntries = Object.entries(condition as Record<string, unknown>);
                        for (const [op, value] of condEntries) {
                            rules.push({
                                id: crypto.randomUUID(),
                                field,
                                operator: op,
                                value,
                            });
                        }
                    }
                }
            }
        }
    } else {
        // Handle simple format: { field: { _op: val } }
        for (const [field, condition] of Object.entries(validation)) {
            if (field !== '_and' && field !== '_or' && typeof condition === 'object' && condition !== null) {
                for (const [op, value] of Object.entries(condition as Record<string, unknown>)) {
                    rules.push({
                        id: crypto.randomUUID(),
                        field,
                        operator: op,
                        value,
                    });
                }
            }
        }
    }

    return rules;
}

// Convert rules back to validation object
function rulesToValidation(rules: ValidationRule[]): Record<string, unknown> | null {
    if (rules.length === 0) return null;

    const andArray = rules
        .filter(rule => rule.field && rule.operator)
        .map(rule => ({
            [rule.field]: {
                [rule.operator]: BOOLEAN_OPERATORS.includes(rule.operator) ? true : rule.value
            }
        }));

    if (andArray.length === 0) return null;

    return { _and: andArray };
}

export function FieldValidationTab({
    permission,
    localPermission,
    policyName,
    onUpdate,
}: FieldValidationTabProps) {
    // Get fields from global store
    const { getFieldsForCollectionSorted, hydrated, loading, hydrate } = useFieldsStore();

    // Force hydrate if needed
    useEffect(() => {
        if (!hydrated && !loading) {
            hydrate(true);
        }
    }, [hydrated, loading, hydrate]);

    // Get fields for this collection
    const fields = useMemo(() => {
        return getFieldsForCollectionSorted(permission.collection);
    }, [getFieldsForCollectionSorted, permission.collection, hydrated]);

    const [rules, setRules] = useState<ValidationRule[]>(() =>
        parseValidationToRules(localPermission.validation as Record<string, unknown>)
    );

    // Update rules when localPermission changes (e.g., when fetched from API)
    useEffect(() => {
        const newRules = parseValidationToRules(localPermission.validation as Record<string, unknown>);
        setRules(newRules);
    }, [localPermission.validation]);

    const handleAddRule = () => {
        const firstField = fields[0];
        const defaultField = firstField?.field || '';
        const fieldType = firstField?.type || 'string';
        const availableOps = getOperatorsForType(fieldType);
        const defaultOp = availableOps.includes('_nnull') ? '_nnull' : availableOps[0] || '_eq';

        const newRule: ValidationRule = {
            id: crypto.randomUUID(),
            field: defaultField,
            operator: defaultOp,
            value: BOOLEAN_OPERATORS.includes(defaultOp) ? true : null,
        };

        const newRules = [...rules, newRule];
        setRules(newRules);
        onUpdate({ validation: rulesToValidation(newRules) });
    };

    const handleRemoveRule = (id: string) => {
        const newRules = rules.filter(r => r.id !== id);
        setRules(newRules);
        onUpdate({ validation: rulesToValidation(newRules) });
    };

    const handleFieldChange = (id: string, newField: string) => {
        const field = fields.find(f => f.field === newField);
        const fieldType = field?.type || 'string';
        const availableOps = getOperatorsForType(fieldType);

        const newRules = rules.map(rule => {
            if (rule.id === id) {
                // Reset operator if current one is not valid for new field type
                const newOp = availableOps.includes(rule.operator) ? rule.operator : availableOps[0] || '_eq';
                return {
                    ...rule,
                    field: newField,
                    operator: newOp,
                    value: BOOLEAN_OPERATORS.includes(newOp) ? true : null,
                };
            }
            return rule;
        });

        setRules(newRules);
        onUpdate({ validation: rulesToValidation(newRules) });
    };

    const handleOperatorChange = (id: string, newOperator: string) => {
        const newRules = rules.map(rule => {
            if (rule.id === id) {
                // Reset value for boolean operators
                const newValue = BOOLEAN_OPERATORS.includes(newOperator)
                    ? true
                    : ARRAY_OPERATORS.includes(newOperator)
                        ? []
                        : null;
                return { ...rule, operator: newOperator, value: newValue };
            }
            return rule;
        });

        setRules(newRules);
        onUpdate({ validation: rulesToValidation(newRules) });
    };

    const handleValueChange = (id: string, newValue: string) => {
        const newRules = rules.map(rule => {
            if (rule.id === id) {
                // Parse value
                let parsedValue: unknown = newValue;

                // Check if it's a dynamic variable
                if (newValue.startsWith('$')) {
                    parsedValue = newValue;
                }
                // Check for boolean
                else if (newValue === 'true') {
                    parsedValue = true;
                } else if (newValue === 'false') {
                    parsedValue = false;
                }
                // Check for number
                else if (!isNaN(Number(newValue)) && newValue !== '') {
                    parsedValue = Number(newValue);
                }
                // For array operators, split by comma
                else if (ARRAY_OPERATORS.includes(rule.operator)) {
                    parsedValue = newValue.split(',').map(v => v.trim()).filter(v => v !== '');
                }

                return { ...rule, value: parsedValue };
            }
            return rule;
        });

        setRules(newRules);
        onUpdate({ validation: rulesToValidation(newRules) });
    };

    // Get available operators for a rule based on its field
    const getOperatorsForRule = (rule: ValidationRule): Array<{ value: string; label: string }> => {
        const field = fields.find(f => f.field === rule.field);
        const fieldType = field?.type || 'string';
        const ops = getOperatorsForType(fieldType);
        return ops.map(op => ({ value: op, label: VALIDATION_OPERATORS[op] || op }));
    };

    // Format value for display in input
    const formatValueForInput = (value: unknown): string => {
        if (value === null || value === undefined) return '';
        if (Array.isArray(value)) return value.join(', ');
        return String(value);
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center py-12">
                <div className="h-8 w-8 animate-spin rounded-full border-2 border-purple-600 border-t-transparent" />
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Notice */}
            <div className="flex items-start gap-3 rounded-lg border border-blue-200 bg-blue-50 p-4">
                <Info className="h-5 w-5 flex-shrink-0 text-blue-600" />
                <div className="text-sm text-blue-800">
                    <p>
                        Validation for <strong>{permission.action}</strong> action with{' '}
                        <strong>{policyName || 'Public'}</strong> policy.
                    </p>
                </div>
            </div>

            {/* Rules List */}
            <div className="rounded-lg border border-gray-200 bg-white p-4">
                {rules.length === 0 ? (
                    <div className="py-4 text-center text-sm text-gray-500">
                        No rules
                    </div>
                ) : (
                    <div className="space-y-3">
                        {rules.map((rule) => {
                            const availableOps = getOperatorsForRule(rule);
                            const showValueInput = !BOOLEAN_OPERATORS.includes(rule.operator);

                            return (
                                <div
                                    key={rule.id}
                                    className="flex flex-wrap items-center gap-2 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2"
                                >
                                    {/* Drag Handle */}
                                    <GripVertical className="h-4 w-4 flex-shrink-0 cursor-grab text-gray-400" />

                                    {/* Field Selector */}
                                    <Select
                                        value={rule.field}
                                        onValueChange={(value) => handleFieldChange(rule.id, value)}
                                    >
                                        <SelectTrigger className="w-[150px] border-0 bg-transparent">
                                            <SelectValue placeholder="Select field" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {fields.length === 0 ? (
                                                <SelectItem value="__no_fields__" disabled>No fields</SelectItem>
                                            ) : (
                                                fields.map((field) => (
                                                    <SelectItem key={field.field} value={field.field}>
                                                        {field.name || field.field}
                                                    </SelectItem>
                                                ))
                                            )}
                                        </SelectContent>
                                    </Select>

                                    {/* Operator Selector */}
                                    <Select
                                        value={rule.operator}
                                        onValueChange={(value) => handleOperatorChange(rule.id, value)}
                                    >
                                        <SelectTrigger className="w-[180px] border-0 bg-purple-100 text-purple-700">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {availableOps.map((op) => (
                                                <SelectItem key={op.value} value={op.value}>
                                                    {op.label}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>

                                    {/* Value Input */}
                                    {showValueInput && (
                                        <Input
                                            type="text"
                                            value={formatValueForInput(rule.value)}
                                            onChange={(e) => handleValueChange(rule.id, e.target.value)}
                                            placeholder={ARRAY_OPERATORS.includes(rule.operator) ? 'value1, value2, ...' : 'Enter value'}
                                            className="min-w-[120px] flex-1"
                                        />
                                    )}

                                    {/* Remove Button */}
                                    <Button
                                        type="button"
                                        variant="ghost"
                                        size="icon"
                                        onClick={() => handleRemoveRule(rule.id)}
                                        className="h-8 w-8 text-gray-400 hover:text-red-500"
                                        title="Remove rule"
                                    >
                                        <X className="h-4 w-4" />
                                    </Button>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            {/* Add Rule Button */}
            <div className="flex gap-2 px-2">
                <Button
                    type="button"
                    variant="ghost"
                    onClick={handleAddRule}
                    className="text-sm font-semibold"
                >
                    <Plus className="h-4 w-4" />
                    Add Filter
                </Button>
            </div>

            {/* Current Value Display (JSON) */}
            <div className="rounded-lg border border-gray-200 bg-gray-100 p-4">
                <pre className="overflow-auto text-xs text-gray-700">
                    {JSON.stringify(localPermission.validation, null, 2) || 'null'}
                </pre>
            </div>
        </div>
    );
}
